import { useEffect, useState } from "react";
import { userRoles } from "@/lib/constants";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", retailer_code: "", address: "", area: "", city: "", state: "", pincode: "", phone: "", email: "", gst: "", salesman: "" };

export default function Retailers() {
  const { user } = useAuth();
  const isAdmin = userRoles(user).includes("admin");
  const [rows, setRows] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const { nameFor } = useUserNames();

  const load = async () => {
    const [{ data }, { data: s }] = await Promise.all([client.get("/retailers"), client.get("/salesmen")]);
    setRows(data); setSalesmen(s);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditId(r.id); setOpen(true); };
  const save = async () => {
    if (!form.name?.trim() || !form.retailer_code?.trim() || !form.gst?.trim())
      return toast.error("Name, Retailer Code and GST are required");
    try {
      if (editId) await client.put(`/retailers/${editId}`, form); else await client.post("/retailers", form);
      toast.success(editId ? "Updated" : "Created"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this retailer?")) return;
    try { await client.delete(`/retailers/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Retailer Master" subtitle="Customer database"
        actions={isAdmin && <Button data-testid="retailer-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} weight="bold" className="mr-1" /> New Retailer</Button>}
      />
      <Section>
        <div className="mb-4 flex items-center gap-3 max-w-xl">
          <Input
            data-testid="retailer-search-input"
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            placeholder="Search by name, code, phone, city, state, salesman…"
            className="rounded-sm bg-white"
          />
          {q && (
            <button
              onClick={()=>setQ("")}
              data-testid="retailer-search-clear"
              className="text-xs font-semibold text-slate-600 hover:text-slate-900"
            >Clear</button>
          )}
          <div className="text-xs text-slate-500" data-testid="retailer-search-count">
            {(() => {
              const f = q.trim().toLowerCase();
              const n = f ? rows.filter(r => [r.name,r.retailer_code,r.phone,r.area,r.city,r.state,r.gst,r.salesman,r.email].some(v => (v||"").toLowerCase().includes(f))).length : rows.length;
              return `${n} / ${rows.length}`;
            })()}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Code</th><th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th><th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">GST</th><th className="px-4 py-3">Salesman</th>
              <th className="px-4 py-3">Address</th><th className="px-4 py-3">Entered By</th>
              {isAdmin && <th className="px-4 py-3 w-24">Actions</th>}
            </tr></thead>
            <tbody data-testid="retailers-tbody">
              {rows.filter(r => {
                const f = q.trim().toLowerCase();
                if (!f) return true;
                return [r.name,r.retailer_code,r.phone,r.area,r.city,r.state,r.gst,r.salesman,r.email].some(v => (v||"").toLowerCase().includes(f));
              }).map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono font-semibold">{r.retailer_code}</td>
                  <td className="px-4 py-2 font-semibold">{r.name}</td>
                  <td className="px-4 py-2 font-mono">{r.phone}</td>
                  <td className="px-4 py-2 text-slate-600">{r.email}</td>
                  <td className="px-4 py-2 font-mono">{r.gst}</td>
                  <td className="px-4 py-2">{r.salesman}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {r.address}
                    {(r.area || r.city || r.state || r.pincode) && (
                      <div className="text-xs text-slate-500">
                        {[r.area, r.city, r.state, r.pincode].filter(Boolean).join(", ")}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs">{nameFor(r.created_by)}</td>
                  {isAdmin && (
                    <td className="px-4 py-2">
                      <div className="flex gap-1">
                        <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-sm"><PencilSimple size={14}/></button>
                        <button onClick={() => remove(r.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="px-4 py-10 text-center text-slate-500">No retailers</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} Retailer</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">Retailer Code *</label><Input data-testid="retailer-code-input" value={form.retailer_code} onChange={(e)=>setForm({...form, retailer_code: e.target.value})}/></div>
              <div><label className="label-cap">Retailer Name *</label><Input data-testid="retailer-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})}/></div>
            </div>
            <div><label className="label-cap">Address</label><Input value={form.address} onChange={(e)=>setForm({...form, address: e.target.value})}/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">Area</label><Input data-testid="retailer-area-input" value={form.area} onChange={(e)=>setForm({...form, area: e.target.value})}/></div>
              <div><label className="label-cap">City</label><Input data-testid="retailer-city-input" value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">State</label><Input data-testid="retailer-state-input" value={form.state} onChange={(e)=>setForm({...form, state: e.target.value})}/></div>
              <div><label className="label-cap">Pincode</label><Input data-testid="retailer-pincode-input" value={form.pincode} onChange={(e)=>setForm({...form, pincode: e.target.value})}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">Phone</label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})}/></div>
              <div><label className="label-cap">Email</label><Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}/></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-cap">GST *</label><Input data-testid="retailer-gst-input" value={form.gst} onChange={(e)=>setForm({...form, gst: e.target.value})}/></div>
              <div>
                <label className="label-cap">Salesman</label>
                <Select value={form.salesman} onValueChange={(v)=>setForm({...form, salesman: v})}>
                  <SelectTrigger data-testid="retailer-salesman-select" className="rounded-sm"><SelectValue placeholder="Select"/></SelectTrigger>
                  <SelectContent>{salesmen.map(s=><SelectItem key={s.mobile} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="retailer-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
